import { COMMAND_CENTER_ROOT } from '../../commandCenterRoutes'
import type { CSSProperties } from 'react'
import OliviumSuiteShell, { accentVar } from './OliviumSuiteShell'
import { ACCESS_CARDS, GOVERNANCE_CARDS, type SuiteCard, type DataReality } from './governanceCards'

export interface OliviumSuiteHomeProps {
  onOpenPath: (to: string) => void
  onHome: () => void
}

const REALITY_LABEL: Record<DataReality, string> = {
  durable: 'Durable',
  durable_read: 'Durable read',
  local_session: 'Local session',
}

const REALITY_ACCENT: Record<DataReality, string> = {
  durable: 'var(--govv-verified)',
  durable_read: 'var(--govv-structure)',
  local_session: 'var(--govv-attention)',
}

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 16,
}

function Card({ card, onOpen }: { card: SuiteCard; onOpen: (to: string) => void }) {
  return (
    <div
      className="olv-gov-card"
      data-clickable="true"
      role="button"
      tabIndex={0}
      aria-label={`Open ${card.title}`}
      onClick={() => onOpen(card.to)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(card.to) }
      }}
      style={{ ['--govv-accent' as string]: accentVar(card.accent), padding: 18, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 148 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: accentVar(card.accent) }}>{card.title}</h3>
        <span className="olv-gov-pill" style={{ color: REALITY_ACCENT[card.reality] }}>
          {REALITY_LABEL[card.reality]}
        </span>
      </div>
      <p style={{ margin: 0, color: 'var(--govv-ink-2)', fontSize: 13, lineHeight: 1.45, flex: 1 }}>{card.blurb}</p>
      <p style={{ margin: 0, color: 'var(--govv-ink-3)', fontSize: 11, lineHeight: 1.4 }}>{card.honesty}</p>
    </div>
  )
}

/**
 * OliviumSuiteHome — the suite landing grid.
 *
 * Ten cards, access surfaces first (already durable) then the seven governance
 * workspaces. Each card is fully clickable (mouse + keyboard) and carries an
 * honest data-reality pill so the grid never implies a backend it lacks.
 */
export default function OliviumSuiteHome({ onOpenPath, onHome }: OliviumSuiteHomeProps) {
  return (
    <OliviumSuiteShell
      title="Olivium Governance"
      subtitle="Trust operations for Square Code — controlled access, audit, integrity, receipts, risk, sharing, and permission review. A working governance workspace, not a read-only console."
      accent="identity"
      honesty="Secure Access, Portal Factory, and Project Portals are durable. The seven governance cards read real durable sources where available (audit trail, integrity chain, receipts, access codes) and run an honestly-labeled local-session ledger for everything not yet backed."
      breadcrumb={[{ label: 'Command Center', to: COMMAND_CENTER_ROOT }, { label: 'Olivium' }]}
      onHome={onHome}
      onOpenPath={onOpenPath}
    >
      <section aria-labelledby="olv-access-h" style={{ marginBottom: 26 }}>
        <h2 id="olv-access-h" style={{ margin: '0 0 12px', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }}>
          Access surfaces
        </h2>
        <div className="olv-gov-hub-grid" style={gridStyle}>
          {ACCESS_CARDS.map((c) => <Card key={c.id} card={c} onOpen={onOpenPath} />)}
        </div>
      </section>

      <section aria-labelledby="olv-gov-h">
        <h2 id="olv-gov-h" style={{ margin: '0 0 12px', fontSize: 13, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }}>
          Governance workspaces
        </h2>
        <div className="olv-gov-hub-grid" style={gridStyle}>
          {GOVERNANCE_CARDS.map((c) => <Card key={c.id} card={c} onOpen={onOpenPath} />)}
        </div>
      </section>
    </OliviumSuiteShell>
  )
}
